import {
    ButtonsOptions,
    HorizontalAlignmentOptions,
    HtmlTagOptions,
    LayoutWidthOptions,
    RatioOptions,
    TextStyleOptions,
    UndefinedOption
} from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { CMS_COMPONENT_CWV_CONFIG } from '../../../catalog/config/component.config';
import { NCmsComponentHeroImage } from './cms-component-hero-image.interface';

export const CMS_COMPONENT_HERO_IMAGE_CONFIG = <Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'>>{
    id: NCmsComponentHeroImage.ID,
    name: 'Hero Image',
    image: '/common/cms/hero-image-component.png',
    isLayout: true,
    description: 'Wide banner with text lines and optional link',
    tags: ['banner', 'link', 'big'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            description:
                'Please use path like /some-path for internal usage or absolute url https://example.com/some-page for external reference'
        },
        // background
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-background',
            title: 'Background'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-2-1'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratioSource',
            type: NICmsContentField.EFieldType.choice,
            title: 'Source image ratio',
            options: RatioOptions,
            default: 'ratio-1-1',
            canShow: (data: NCmsComponentHeroImage.IData) => data?.ratio === 'ratio-full-page'
        },
        {
            key: 'background_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image'
        },
        // video
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-positioning',
            title: 'Video'
        },
        {
            key: 'video_id',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL'
        },
        <NICmsContentField.IChoiceField>{
            key: 'video_provider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Provider',
            options: [
                ...UndefinedOption,
                {
                    key: 'youtube',
                    title: 'YouTube'
                },
                {
                    key: 'vimeo',
                    title: 'Vimeo'
                }
            ],
            description: 'Choose a video provider'
        },
        {
            key: 'video_autoplay',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Autoplay',
            default: true
        },
        {
            key: 'video_background',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Background Mode',
            default: true
        },
        {
            key: 'video_hide',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide',
            default: false
        },
        {
            key: 'button_hide',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide Play/Pause Button',
            default: false
        },
        // content
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: 'Content'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            key: 'width',
            itemName: 'width',
            options: LayoutWidthOptions,
            default: undefined
        },
        // Text blocks
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'blocks',
            title: 'Content Blocks',
            itemName: 'Block',
            itemNameField: 'block',
            defaultSize: 1,
            fields: [
                // general
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-general',
                    title: 'General'
                },
                {
                    key: 'custom',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Use Custom Components',
                    default: false
                },
                <NICmsContentField.IComponentsField>{
                    key: 'components',
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    default: [],
                    canShow: (data: NCmsComponentHeroImage.IBlock) => data?.custom
                },
                {
                    key: 'style',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Custom Styling',
                    description: 'Direct CSS. Please be careful when changing this.',
                    canShow: (data: NCmsComponentHeroImage.IBlock) => !data?.custom
                },
                // text positioning
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-positioning',
                    title: 'Positioning'
                },
                <NICmsContentField.INumberField>{
                    key: 'left_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Left',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 0
                },
                <NICmsContentField.INumberField>{
                    key: 'top_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Top',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 50
                },
                // text lines
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'lines',
                    title: 'Text Lines',
                    itemName: 'Line',
                    itemNameField: 'text',
                    canShow: (data: NCmsComponentHeroImage.IBlock) => !data?.custom,
                    defaultSize: 1,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Line Type',
                            options: [
                                { key: 'text', title: 'Text Line' },
                                { key: 'button', title: 'Button' },
                                { key: 'countdown', title: 'Countdown' }
                            ],
                            description: 'Line type',
                            default: 'text'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'align',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Alignment',
                            options: HorizontalAlignmentOptions,
                            default: 'left'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'button_class',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Button Style',
                            canShow: (data: NCmsComponentHeroImage.ILine) => data?.type === 'button',
                            options: ButtonsOptions,
                            default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined
                        },
                        {
                            key: 'button_link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Button Link',
                            description: 'If component link is set it will be used unless overridden here.',
                            canShow: (data: NCmsComponentHeroImage.ILine) => data?.type === 'button'
                        },
                        <NICmsContentField.IDatetimeField>{
                            key: 'end_date',
                            type: NICmsContentField.EFieldType.datetime,
                            title: 'End date',
                            description: 'Format of the date: YYYY-MM-DD',
                            canShow: data => data?.type === 'countdown'
                        },
                        {
                            key: 'text',
                            type: NICmsContentField.EFieldType.textarea,
                            title: 'Copy',
                            canShow: (data: NCmsComponentHeroImage.ILine) => data?.type !== 'countdown'
                        },
                        {
                            key: 'copyLink',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Copy Link'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'text_style',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Style',
                            options: TextStyleOptions,
                            canShow: (data: NCmsComponentHeroImage.ILine) => data?.type !== 'button'
                        },
                        {
                            key: 'style',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Custom Styling',
                            description: 'Direct CSS. Please be careful when changing this.',
                            protected: true
                        },
                        <NICmsContentField.IField>{
                            key: 'color',
                            type: NICmsContentField.EFieldType.colorPicker,
                            title: 'Text Color',
                            canShow: (data: NCmsComponentHeroImage.ILine) =>
                                data?.type !== 'button' || data?.button_class === 'button-inline'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'tag',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Force Tag',
                            description: 'For SEO reasons you may want to force particular HTML tag',
                            options: HtmlTagOptions,
                            canShow: (data: NCmsComponentHeroImage.ILine) => data?.type !== 'button' && data?.type !== 'countdown'
                        }
                    ]
                }
            ]
        },
        ...CMS_COMPONENT_CWV_CONFIG
    ]
};
